'use client'

import { RefObject } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsapSetup } from './gsapSetup'
import { useReducedMotion } from './useReducedMotion'

/**
 * Solution section — horizontal core-sample panorama.
 *
 * Pins the section while the 6000px inner track translates horizontally
 * under it. A plateau ("dwell") sits on the timeline right around the
 * video panel so the scroll slows and the frame holds centre-screen for
 * a moment. Per-panel text reveals are scroll-tied via ScrollTrigger's
 * `containerAnimation` so they fire exactly as each panel arrives on
 * stage. Root-system stroke draw-on uses the same pattern.
 *
 * Disabled when (a) reduced motion is requested or (b) the device is
 * touch-only — a mobile stacked layout takes over instead (handled by
 * matching CSS in `Solution.tsx`).
 */

// px of extra scroll around the video centre where the x translation
// holds — creates the "linger" feel without hijacking input.
const DWELL_PX = 320

// px of leading scroll where the track stays pinned at x=0 so the
// reader can absorb the intro panel before horizontal motion begins.
// Keep this small — it should feel like a beat, not a stop.
const START_PAUSE_PX = 220

// Multiplier that stretches the pin-scroll length so the horizontal
// track reads at a calmer pace. Increase to slow the section down,
// decrease to speed it up.
const SCROLL_SCALE = 1.45

export function useSolutionScene(ref: RefObject<HTMLElement | null>) {
  const reduced = useReducedMotion()

  useGSAP(
    () => {
      gsapSetup()

      const root = ref.current
      if (!root) return

      const track  = root.querySelector<HTMLElement>('[data-sol-track]')
      const video  = root.querySelector<HTMLElement>('[data-panel="video"]')
      const fill   = root.querySelector<HTMLElement>('[data-progress-fill]')
      const dot    = root.querySelector<HTMLElement>('[data-progress-dot]')
      if (!track || !video) return

      const isTouch =
        typeof window !== 'undefined' &&
        window.matchMedia('(max-width: 767px), (hover: none) and (pointer: coarse)').matches

      // Mobile / reduced motion: don't pin, let the CSS stacked layout
      // just scroll vertically. Still reveal panel content on enter.
      if (reduced || isTouch) {
        const panels = gsap.utils.toArray<HTMLElement>('[data-panel]', root)
        panels.forEach((panel) => {
          const items = gsap.utils.toArray<HTMLElement>('[data-anim]', panel)
          if (items.length === 0) return
          gsap.set(items, { autoAlpha: 0, y: 24 })
          gsap.to(items, {
            autoAlpha: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
            stagger: 0.06,
            scrollTrigger: {
              trigger: panel,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          })
        })
        return
      }

      // ── Compute geometry ──────────────────────────────────────────────
      // Function-form so they re-evaluate on ScrollTrigger refresh (window
      // resize, font load, pin spacer insertion, etc.).
      const getTrackWidth   = () => track.scrollWidth
      const getViewport     = () => window.innerWidth
      const getTranslate    = () => getTrackWidth() - getViewport()
      const getVideoCenterX = () =>
        video.offsetLeft + video.offsetWidth / 2
      const getXAtVideoCenter = () =>
        -(getVideoCenterX() - getViewport() / 2)

      const totalScroll = () => getTranslate() + DWELL_PX + START_PAUSE_PX

      // ── Master horizontal timeline (scrubbed, pinned) ────────────────
      // Segments: [start-pause], [0 → video-centre], [video-dwell],
      // [video-centre → end]. Pause/dwell tweens are literal
      // `gsap.to({}, { duration })` calls that consume scroll distance
      // without moving the track.
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: root,
          start: 'top top',
          end:   () => `+=${totalScroll() * SCROLL_SCALE}`,
          pin:   true,
          anticipatePin: 1,
          scrub: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const p = self.progress
            if (fill) fill.style.width   = `${p * 100}%`
            if (dot)  dot.style.left     = `${p * 100}%`
          },
        },
      })

      tl.to({}, { duration: () => START_PAUSE_PX / totalScroll() }, 0)
      tl.to(track, {
        x: getXAtVideoCenter,
        duration: () => -getXAtVideoCenter() / totalScroll(),
      })
      tl.to({}, { duration: () => DWELL_PX / totalScroll() })
      tl.to(track, {
        x: () => -getTranslate(),
        duration: () =>
          (getTranslate() + getXAtVideoCenter()) / totalScroll(),
      })

      // ── Per-panel text reveals (scroll-tied via containerAnimation) ──
      const panels = gsap.utils.toArray<HTMLElement>('[data-panel]', root)
      panels.forEach((panel) => {
        const items = gsap.utils.toArray<HTMLElement>('[data-anim]', panel)
        if (items.length === 0) return
        gsap.set(items, { autoAlpha: 0, y: 32 })
        gsap.to(items, {
          autoAlpha: 1,
          y: 0,
          duration: 0.7,
          ease: 'power2.out',
          stagger: 0.07,
          scrollTrigger: {
            trigger: panel,
            containerAnimation: tl,
            start: 'left 85%',
            end:   'right 20%',
            toggleActions: 'play none none reverse',
          },
        })
      })

      // ── Root SVG draw-on (Strat 03 + 04) ──────────────────────────────
      const rootPaths = gsap.utils.toArray<SVGPathElement>('[data-root-path]', root)
      rootPaths.forEach((path) => {
        const len = path.getTotalLength()
        gsap.set(path, { strokeDasharray: len, strokeDashoffset: len })
        const panel = path.closest<HTMLElement>('[data-panel]')
        if (!panel) return
        gsap.to(path, {
          strokeDashoffset: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: panel,
            containerAnimation: tl,
            start: 'left 90%',
            end:   'right 30%',
            scrub: true,
          },
        })
      })

      // ── Bacteria particles (Strat 02 onwards): ambient drift ─────────
      gsap.utils.toArray<HTMLElement>('[data-bact]', root).forEach((p, i) => {
        gsap.to(p, {
          x: `+=${(Math.random() * 2 - 1) * 14}`,
          y: `+=${(Math.random() * 2 - 1) * 14}`,
          duration: 3 + (i % 5) * 0.6,
          ease: 'sine.inOut',
          yoyo: true,
          repeat: -1,
          delay: (i * 0.13) % 2,
        })
      })

      ScrollTrigger.refresh()
    },
    { scope: ref, dependencies: [reduced], revertOnUpdate: true },
  )
}
